import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import { Wallet, ShieldCheck, Percent, Calculator } from 'lucide-react';
import { BottomSheet } from '../../components/common/modal/BottomSheet';

import Breadcrumbs from '../../CommonElements/Breadcrumbs';
import { LoanRequestForm } from './Components/LoanRequestForm';
import { LoanRequestInfo } from './Components/LoanRequestInfo';
import { useLoanRequest } from '../../Hooks/LoanRequest/useLoanRequest';
import H2 from '../../CommonElements/Headings/H2Element';
import './loanRequest.scss';

const LoanRequest = () => {
   const loanRequest = useLoanRequest();
   const { data, isLoading } = loanRequest.query;

   const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
   const [isSimulatorOpen, setIsSimulatorOpen] = useState(false);

   useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth < 768);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
   }, []);

   const creditLimit = data?.credit_limit || 0;
   const totalInvestment = data?.total_investment || 0;
   const interestRate = (loanRequest.MonthlyRate * 100) || 0;

   return (
      <div className="page-body loan-request-container">
         {!isMobile && (
            <Breadcrumbs
               pageTitle="Solicitud de Préstamos"
               parent="Sociedad"
               title="Préstamos"
            />
         )}
         <Container className="general-widget" fluid>
            {/* KPI Cards Row (Responsive, unified for Mobile & Desktop) */}
            <Row className="mb-4 gy-3 kpi-widgets-row loan-request-kpi-row">
               <Col xs={4} md={4} className="animate-fade-in delay-1">
                  <Card className="kpi-card border-0 mb-0 kpi-card-blue">
                     <CardBody className="p-4 d-flex align-items-center justify-content-between">
                        <div className="d-flex flex-column gap-1">
                           <span className="kpi-title">
                              <span className="d-none d-md-inline">Cupo Crédito Disponible</span>
                              <span className="d-inline d-md-none">Cupo</span>
                           </span>
                           <h4 className="kpi-value mb-0">
                              {isLoading ? (
                                 '...'
                              ) : (
                                 `$${creditLimit.toLocaleString('es-ES', {
                                    maximumFractionDigits: 0,
                                 })}`
                              )}
                           </h4>
                        </div>
                        <div className="kpi-icon-wrapper primary">
                           <Wallet size={20} />
                        </div>
                     </CardBody>
                  </Card>
               </Col>

               <Col xs={4} md={4} className="animate-fade-in delay-2">
                  <Card className="kpi-card border-0 mb-0 kpi-card-green">
                     <CardBody className="p-4 d-flex align-items-center justify-content-between">
                        <div className="d-flex flex-column gap-1">
                           <span className="kpi-title">
                              <span className="d-none d-md-inline">Inversión Respaldada</span>
                              <span className="d-inline d-md-none">Respaldo</span>
                           </span>
                           <h4 className="kpi-value mb-0">
                              {isLoading ? (
                                 '...'
                              ) : (
                                 `$${totalInvestment.toLocaleString('es-ES', {
                                    maximumFractionDigits: 0,
                                 })}`
                              )}
                           </h4>
                        </div>
                        <div className="kpi-icon-wrapper success">
                           <ShieldCheck size={20} />
                        </div>
                     </CardBody>
                  </Card>
               </Col>

               <Col xs={4} md={4} className="animate-fade-in delay-3">
                  <Card className="kpi-card border-0 mb-0 kpi-card-purple">
                     <CardBody className="p-4 d-flex align-items-center justify-content-between">
                        <div className="d-flex flex-column gap-1">
                           <span className="kpi-title">
                              <span className="d-none d-md-inline">Tasa de Interés Mensual</span>
                              <span className="d-inline d-md-none">Tasa</span>
                           </span>
                           <h4 className="kpi-value mb-0">
                              {isLoading ? (
                                 '...'
                              ) : (
                                 `${interestRate.toLocaleString('es-ES', {
                                    minimumFractionDigits: 2,
                                 })}%`
                              )}
                           </h4>
                        </div>
                        <div className="kpi-icon-wrapper warning">
                           <Percent size={20} />
                        </div>
                     </CardBody>
                  </Card>
               </Col>
            </Row>

            {isMobile && (
               <div className="page-title mb-2 animate-fade-in delay-2">
                  <H2>Solicitud de Préstamos</H2>
               </div>
            )}

            {/* Split layout for desktop, stacked on mobile */}
            <Row>
               <Col xl={7} lg={7} md={12} className="animate-fade-in delay-4 mb-4">
                  <LoanRequestForm onSimulateClick={isMobile ? () => setIsSimulatorOpen(true) : undefined} />
               </Col>
               {!isMobile && (
                  <Col xl={5} lg={5} md={12} className="animate-fade-in delay-4 mb-4">
                     <div className="sticky-summary">
                        <LoanRequestInfo />
                     </div>
                  </Col>
               )}
            </Row>

            {/* Mobile Bottom Sheet Modal for Simulator */}
            {isMobile && (
               <BottomSheet
                  isOpen={isSimulatorOpen}
                  onClose={() => setIsSimulatorOpen(false)}
                  title="Simulador de Cuotas"
               >
                  <LoanRequestInfo isModal={true} hideHeader={true} onClose={() => setIsSimulatorOpen(false)} />
               </BottomSheet>
            )}
         </Container>
      </div>
   );
};

export default LoanRequest;
